import { PaymentElement } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { BankCard, DefaultModal, StyledText } from 'UI';
import Button from 'UI/Button';
import { TermEnum } from 'UI/PriceLabel/PriceLabel';
import {
  CheckboxSkeletonWrapper,
  CheckboxSquareSkeleton,
  CouponAppliedIconSkeleton,
  CouponAppliedSkeleton,
  CouponAppliedTextSkeleton,
  CouponBoxSkeleton,
  CouponSkeletonContainer,
  InstallmentPriceSkeleton,
  LifetimeBoxSizerRow,
  LifetimeCouponAppliedSkeleton,
  LifetimeCouponBoxSkeleton,
  PriceSkeleton,
  PricingRowSkeleton,
  RemoveCouponButtonSkeleton,
} from 'UI/Skeletons/CheckoutSkeletons';
import { CouponDiscountTypeEnum, SubscribeDtoPeriodEnum, SubscribeDtoTypeEnum } from 'api/generated';
import { PaymentsSystemsEnums, getLevelName } from 'constant';
import { useAsyncAction } from 'hooks';
import { useCoupon } from 'hooks';
import { useStripeDefault } from 'hooks/useStripeDefault';
import { MonetizationLightModeProvider, StripeProvider } from 'providers';
import { FC, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import { PaymentOptions } from 'store/ducks/courses/types';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { BaseModalProps } from 'types';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

import { Coupon, CouponForCertificate, Price, PriceInstallment, Secure } from './components';
import { SubscribeDtoType } from './components/CouponForCertificate/components/SuccessCoupon/SuccessCoupon';

declare const dataLayer: any;

type PaymentModalProps = {
  courseId: string;
  subscribeType: SubscribeDtoTypeEnum;
  term: TermEnum;
  amount: number;
  isActivate: boolean | undefined;
  oldAmount?: number;
  isCertificate: boolean;
  isUpdate: boolean;
  isTrial: boolean;
  couponCode: string | undefined;
  firstMonthDiscountId?: string;
  firstMonthDiscountCode?: string;
  firstMonthDiscountAmount?: number;
  firstMonthDiscountType?: CouponDiscountTypeEnum;
  firstMonthDiscountPromoCode?: string;
  openSuccessModal: () => void;
  /** Present only when a previous step exists (the installments-or-lifetime choice); renders the back arrow. */
  onBack?: () => void;
} & BaseModalProps;

const PaymentModal: FC<PaymentModalProps> = ({
  onClose,
  onBack,
  openSuccessModal,
  courseId,
  subscribeType,
  isCertificate,
  isActivate,
  term,
  amount,
  oldAmount,
  isUpdate,
  isTrial,
  couponCode,
  firstMonthDiscountId,
  firstMonthDiscountCode: _firstMonthDiscountCode,
  firstMonthDiscountAmount: firstMonthDiscountAmountProp,
  firstMonthDiscountType: firstMonthDiscountTypeProp,
  firstMonthDiscountPromoCode: firstMonthDiscountPromoCodeProp,
  ...props
}) => {
  const planLevelName = getLevelName(subscribeType, isCertificate ? 'certificate' : 'default');
  const dispatch = useAppDispatch();
  const initializedFirstMonthDiscountRef = React.useRef(false);
  const [getCourseByIdAction] = useAsyncAction(actions.courses.getCourseById);
  const [transactionId, setTransactionId] = useState<string | undefined>(undefined);
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [isLoadingChangeFirstCoupon, setIsLoadingChangeFirstCoupon] = useState<boolean>(false);
  const [invoiceId, setInvoiceId] = useState('');
  const [pendingCouponCode, setPendingCouponCode] = useState<string | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [courseName, setCourseName] = useState<string | undefined>('');
  /** True once getCourseById resolved OR failed — a dead fetch must not pin the skeleton forever. */
  const [courseFetchDone, setCourseFetchDone] = useState(false);
  /** Latched: once the header has shown real content it never falls back to a skeleton. */
  const [headerReady, setHeaderReady] = useState(false);
  const user = useAppSelector(selectors.user.selectUser);
  const paymentMethod = useMemo(() => user?.paymentMethod, []);
  const paymentOption = useAppSelector(selectors.courses.selectPaymentOption);
  const isPaymentPlan = paymentOption === PaymentOptions.PAYMENT_PLAN;
  const [certificateFinalPrice, setCertificateFinalPrice] = useState<any>();
  const [getMe] = useAsyncAction(actions.user.getMe);

  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const selectedCourse = useAppSelector(selectors.courses.selectCourse);
  // const signUpModalCourse = useAppSelector(selectors.courses.selectPublicCourse);
  const course = selectedCourse || activeCourse;

  // The store course is authoritative when it matches the checkout's courseId (bundle flow and
  // deep-links pre-select it); the refetch merely confirms it. Seeding from the store removes the
  // blank-title frame those paths used to paint.
  const resolvedCourseName = courseName || (course?.id === courseId ? course?.name ?? '' : '');

  const useFirstMonthDiscount = useAppSelector(selectors.courses.selectUseFirstMonthDiscount);
  const direction = useAppSelector(selectors.direction.selectDirection);
  const termOfUseUrl = direction?.termsOfServiceUrl;
  const privacyPolicyUrl = direction?.privacyPolicyUrl;
  const courseCheckoutLogoUrl =
    typeof (course as any)?.courseCheckoutLogoUrl === 'string'
      ? ((course as any).courseCheckoutLogoUrl as string).trim()
      : undefined;
  // Fallback chain ends at the direction's MAIN logo: every direction has one (it is the site
  // header mark), while checkoutLogoUrl is optional — so checkout always shows a brand mark even
  // for courses and directions with no dedicated checkout art.
  const logo = courseCheckoutLogoUrl || direction?.checkoutLogoUrl || direction?.logoUrl;

  const resolvedFirstMonthDiscountId =
    firstMonthDiscountId ??
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCouponId;

  const resolvedFirstMonthDiscountAmount = Number(
    firstMonthDiscountAmountProp ??
      // @ts-ignore
      course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCoupon?.discountAmount ??
      0,
  );

  const resolvedFirstMonthDiscountType =
    firstMonthDiscountTypeProp ??
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCoupon?.discountType;

  const resolvedFirstMonthDiscountPromoCode =
    firstMonthDiscountPromoCodeProp ??
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCoupon?.promoCode;

  const isAmountFirstDiscountType = resolvedFirstMonthDiscountType === CouponDiscountTypeEnum.Amount;

  // The white pricing box's final row count is knowable at mount — SuccessCoupon renders 4 fixed
  // rows (Product Total, Order Total, Initial Payment, +11 Payments) plus one each for the main
  // coupon, the first-month coupon, and the day-5 commitment when configured. Mirror its
  // conditions so the skeleton reserves the exact final height (a 6-row guess grew ~27px on
  // 7-row bundle checkouts).
  // @ts-ignore
  const installmentPriceForLevel = course?.prices?.installment?.[subscribeType];
  const expectCommitmentRow =
    installmentPriceForLevel?.earlyFollowUpPaymentEnabled === true &&
    Number(installmentPriceForLevel?.earlyFollowUpPaymentAmount ?? 0) > 0 &&
    Number(installmentPriceForLevel?.earlyFollowUpPaymentDelayDays ?? 0) > 0;
  const expectedPricingRows =
    4 +
    (couponCode ? 1 : 0) +
    (resolvedFirstMonthDiscountId && resolvedFirstMonthDiscountPromoCode && resolvedFirstMonthDiscountAmount > 0
      ? 1
      : 0) +
    (expectCommitmentRow ? 1 : 0);

  useEffect(() => {
    if (
      !initializedFirstMonthDiscountRef.current &&
      isCertificate &&
      isPaymentPlan &&
      resolvedFirstMonthDiscountId &&
      !useFirstMonthDiscount
    ) {
      dispatch(actions.courses.setUseFirstMonthDiscount(true));
      initializedFirstMonthDiscountRef.current = true;
      return;
    }

    if (
      !initializedFirstMonthDiscountRef.current &&
      (!isCertificate || !isPaymentPlan || !resolvedFirstMonthDiscountId)
    ) {
      // The flag is global redux state and nothing clears it on close — a plan checkout leaves it
      // true, and the back arrow makes plan -> lifetime reachable in one session, where the stale
      // flag subtracted the first-month discount from the ONE-TIME header ($799 showed as $713).
      if (useFirstMonthDiscount) {
        dispatch(actions.courses.setUseFirstMonthDiscount(false));
      }
      initializedFirstMonthDiscountRef.current = true;
    }
  }, [dispatch, isCertificate, isPaymentPlan, resolvedFirstMonthDiscountId, useFirstMonthDiscount]);

  const {
    couponState,
    isInvalidCoupon,
    removeCoupon,
    onCouponSubmit,
    setIsInvalidCoupon,
    isDefaultCoupon,
    onChangePromoCode,
    isLoading,
    isCertificateLoading,
    isCheckPromoCodeLoading,
    isChangePromoCodeLoading,
  } = useCoupon({
    courseId,
    subscribeType,
    term,
    amount,
    defaultCouponCode: couponCode || '',
    invoiceId,
    isCertificate,
    setClientSecret,
    setInvoiceId,
    isUpdate,
    isPurchase: term === TermEnum.LIFE_TIME,
  });

  const { couponErrorMessage, isSuccessCoupon, discountAmount, discountValue, promoCode } = couponState;

  /**
   * Header skeleton gate — same idea as the direct-checkout links (SignUpModal): the branded
   * header shows skeletons only while content that WILL arrive is still in flight, and never
   * flashes when everything is already local.
   *   name settled:   we have a name to show, or the fetch is over either way
   *   coupon settled: no default coupon to check, or the check resolved (success OR invalid)
   * `isCheckPromoCodeLoading` is deliberately not used: it is still false on the first frames,
   * before CouponForCertificate's auto-submit effect runs, and would flash the real content in.
   */
  const isNameSettled = Boolean(resolvedCourseName) || courseFetchDone;
  const isCouponSettled = !couponCode || isSuccessCoupon || isInvalidCoupon;
  useEffect(() => {
    if (isNameSettled && isCouponSettled) {
      setHeaderReady(true);
    }
  }, [isNameSettled, isCouponSettled]);
  // Computed (not just the latch) so paths with nothing to load skip the skeleton synchronously.
  const isHeaderLoading = !(headerReady || (isNameSettled && isCouponSettled));

  const {
    clientSecretStripe,
    handleSubmitStripe,
    isLoadingStripe,
    errorMessageStripe,
    isSuccessStripe,
    isDisabledStripe,
  } = useStripeDefault({
    stripe,
    elements,
    courseId,
    promocode: promoCode,
    period: term as SubscribeDtoPeriodEnum,
    level: subscribeType,
    source: 'application',
    isTrial,
    isCertificate,
    useFirstMonthDiscount,
    isUpdate,
    isPurchase: term === TermEnum.LIFE_TIME,
    courseName: resolvedCourseName,
    amount,
    subscribeType,
    clientSecret,
    setInvoiceId,
    setClientSecret,
    setTransactionId,
    isActivate,
  });

  const getUserInfo = async () => {
    try {
      await getMe();
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    await handleSubmitStripe(e);
    if (!user?.paymentMethod) {
      getUserInfo();
    }
  };

  const handleChangePromoCodeWithDeferredApply = async (data: { coupon?: string }) => {
    const normalizedCoupon = (data?.coupon || '').trim();

    if (!normalizedCoupon) {
      await onChangePromoCode(data);
      return;
    }

    if (!invoiceId) {
      setPendingCouponCode(normalizedCoupon);
      return;
    }

    await onChangePromoCode({ coupon: normalizedCoupon });
    setPendingCouponCode(null);
  };

  useEffect(() => {
    if (!pendingCouponCode || !invoiceId || isChangePromoCodeLoading) {
      return;
    }

    onChangePromoCode({ coupon: pendingCouponCode });
    setPendingCouponCode(null);
  }, [pendingCouponCode, invoiceId, isChangePromoCodeLoading, onChangePromoCode]);

  /**
   * Header display discount, clamped by an invariant instead of trusting accumulated state: an
   * amount-type coupon can never discount more than its own amount. Coupon state is written from
   * several places (auto-apply, first-month toggle, persisted store) and has been observed to
   * arrive polluted — $799 + a first-month $86 leaking into a ONE-TIME header, showing $713 while
   * the server-side summary correctly said $799. Percentage coupons pass through untouched
   * (their discountAmount is a rate, not a ceiling).
   */
  // discountAmount > 100 distinguishes dollar coupons from percentage rates (a rate is at most
  // 100); only dollar coupons get the ceiling, so a 70%-off coupon is never clamped to "$70".
  const displayDiscountValue =
    discountValue && discountAmount && discountValue > discountAmount && discountAmount > 100
      ? discountAmount
      : discountValue;

  const finalPrice = displayDiscountValue && amount ? amount - displayDiscountValue : amount;

  const closeModal = () => {
    if (!isLoadingStripe) {
      onClose();
    }
  };

  const getCourseById = async () => {
    if (courseId) {
      try {
        const res = await getCourseByIdAction(courseId);
        setCourseName(res.name);
      } catch (error) {
        console.log(error);
      } finally {
        setCourseFetchDone(true);
      }
    } else {
      setCourseFetchDone(true);
    }
  };
  // prettier-ignore
  const pushDataLayer = async () => {
    if (!clientSecret) {
      return;
    }

    // Get payment intent directly from Stripe to ensure we have the ID
    const paymentIntent = (await stripe?.retrievePaymentIntent(clientSecret))?.paymentIntent;

    const lifetimeValue = calculateLifetimeValue();
    console.log('before PaymentModal pushDataLayer');
    dataLayer.push({
      event: 'successful_purchase',
      transactionId: paymentIntent?.id || transactionId,
      course_name: resolvedCourseName,
      promo_code: promoCode,
      item_name: subscribeType,
      item_id: courseId,
      //price: finalPrice,
      value: Number(lifetimeValue), // Use lifetime value even in error case
      term: term,
      enhanced_conversion_data: {
        email: user?.email,
        gclid: user?.googleClickId || ''
      },
      gclid: user?.googleClickId || ''
    });

    // Helper function to calculate lifetime value consistently
    function calculateLifetimeValue() {
      // Check if this is a bundle
      const isBundle = (course as any)?.type === 'bundle';
      
      // For bundle installment plans - report 80% of total value
      if (isBundle && isPaymentPlan) {
        // Get the total price before any discount
        const totalAmount = amount - (discountValue || 0);

        // For installment plan with first month discount
        if (useFirstMonthDiscount && resolvedFirstMonthDiscountAmount) {
          const perMonth = totalAmount / 12;
          const firstMonthDiscount = isAmountFirstDiscountType
            ? resolvedFirstMonthDiscountAmount
            : (perMonth * resolvedFirstMonthDiscountAmount) / 100;

          // Total amount with first month discount applied, then 80% for bundles
          return (totalAmount - firstMonthDiscount) * 0.8;
        } else {
          // Return 80% of total amount for bundles
          return totalAmount * 0.8;
        }
      }
      // For installment courses (certificate with payment plan)
      else if (isCertificate && isPaymentPlan) {
        // Get the total price before any discount
        const totalAmount = amount - (discountValue || 0);

        // For installment plan with first month discount
        if (useFirstMonthDiscount && resolvedFirstMonthDiscountAmount) {
          const perMonth = totalAmount / 12;
          const firstMonthDiscount = isAmountFirstDiscountType
            ? resolvedFirstMonthDiscountAmount
            : (perMonth * resolvedFirstMonthDiscountAmount) / 100;

          // Total amount with first month discount applied
          return totalAmount - firstMonthDiscount;
        } else {
          return totalAmount;
        }
      } else if (term === TermEnum.MONTHLY) {
        // For monthly subscriptions, calculate annual value (multiply by 2.5 for expected retention)
        return finalPrice * 2.5;
      } else if (term === TermEnum.YEARLY) {
        // For yearly subscriptions, value is already annual
        return finalPrice;
      }

      // For LIFETIME, keep the finalPrice as is
      return finalPrice;
    }
  };

  useEffect(() => {
    if (isSuccessStripe) {
      openSuccessModal();
      pushDataLayer();
    }
  }, [isSuccessStripe]);

  useEffect(() => {
    getCourseById();
  }, [courseId]);

  // Debug logging for bundle installment issue
  console.log('PaymentModal Debug:', {
    term,
    termType: typeof term,
    termString: String(term),
    isCertificate,
    isInstallmentComparison: String(term) === 'installment',
  });

  // Calculate installment term like SignUpModal does
  const isInstallmentTerm = String(term) === 'installment';

  return (
    <MonetizationLightModeProvider>
      <DefaultModal
        {...props}
        onRequestClose={closeModal}
        containerCSS={ContainerCSS}
        closeButtonCSS={CloseButton}
        showBackButton={Boolean(onBack)}
        onBackIconClick={onBack}
        backButtonCSS={BackButton}
        iconColor="#FFF"
        modalCSS={ModalCSS}>
        <PaymentWrapper>
          {/* The logo's only real source is the direction payload; when the direction is stale
              or unresolved (localhost, persisted store) an <img src={undefined}> renders a broken
              frame — hide the block instead. */}
          {logo && (
            <LogoContainer>
              <LogoImg src={logo} />
            </LogoContainer>
          )}

          {/* Same treatment as the direct-checkout links (SignUpModal): while the default-coupon
              check / course fetch are in flight, skeletons hold the exact footprint so nothing
              displaces when the real numbers land. The title renders live in both states — it is
              prop/store-derived and instant on every entry path. */}
          {isHeaderLoading && (
            <>
              {isCertificate ? <InstallmentPriceSkeleton /> : <PriceSkeleton />}

              <NameContainer>
                <CourseNameText>{resolvedCourseName.split(' — ')[0]}</CourseNameText>
                <CourseSubNameText>{planLevelName}</CourseSubNameText>
              </NameContainer>
              {resolvedCourseName.includes(' — ') && (
                <CourseMembersText>{resolvedCourseName.split(' — ').slice(1).join(' — ')}</CourseMembersText>
              )}

              {isCertificate && isPaymentPlan ? (
                <CouponSkeletonContainer>
                  {resolvedFirstMonthDiscountId && (
                    <CheckboxSkeletonWrapper>
                      <CheckboxSquareSkeleton />
                    </CheckboxSkeletonWrapper>
                  )}

                  <CouponBoxSkeleton $rows={expectedPricingRows}>
                    {Array.from({ length: expectedPricingRows }).map((_, index) => (
                      <PricingRowSkeleton key={index} />
                    ))}
                  </CouponBoxSkeleton>

                  {/* Without a default coupon the real content here is the coupon INPUT form,
                      not the applied line — don't fake a row that resolves into something else. */}
                  {couponCode && (
                    <CouponAppliedSkeleton>
                      <CouponAppliedIconSkeleton />
                      <CouponAppliedTextSkeleton />
                      <RemoveCouponButtonSkeleton />
                    </CouponAppliedSkeleton>
                  )}
                </CouponSkeletonContainer>
              ) : (
                // Without a default coupon the lifetime branch renders only the "Add coupon"
                // link — a white-box skeleton would vanish into nothing when loading ends.
                couponCode && (
                  <>
                    <LifetimeCouponBoxSkeleton>
                      {/* Invisible replica of the real box's widest row ("<name> <level>  $<amount>",
                          40px flex gap) so the skeleton box matches the content-sized real box's
                          width instead of jumping when it lands. */}
                      <LifetimeBoxSizerRow aria-hidden>
                        <span>{`${resolvedCourseName} ${planLevelName}`}</span>
                        <span>{`$${amount.toLocaleString('en-US')}`}</span>
                      </LifetimeBoxSizerRow>
                      <PricingRowSkeleton />
                      <PricingRowSkeleton />
                    </LifetimeCouponBoxSkeleton>

                    <LifetimeCouponAppliedSkeleton>
                      <CouponAppliedIconSkeleton />
                      <CouponAppliedTextSkeleton />
                      <RemoveCouponButtonSkeleton />
                    </LifetimeCouponAppliedSkeleton>
                  </>
                )
              )}
            </>
          )}

          {/* Kept MOUNTED (hidden, not unmounted) while skeletons show: unlike SignUpModal, the
              default-coupon check here is fired by CouponForCertificate/Coupon's own mount effect
              — unmounting them would leave the gate waiting on a fetch that never starts. */}
          <HeaderContent $hidden={isHeaderLoading}>
            {isCertificate ? (
              <PriceInstallment
                amount={amount}
                /* Gated on the plan term too: the redux flag can be stale-true for one render
                   before the reset effect runs, and a one-time price must never subtract a
                   monthly first-month coupon (regression: $799 lifetime rendered as $713). */
                isFirstDiscount={useFirstMonthDiscount && isPaymentPlan}
                firstMonthDiscountAmount={resolvedFirstMonthDiscountAmount}
                isAmountFirstDiscountType={isAmountFirstDiscountType}
                discountValue={discountValue}
                isDefaultCoupon={isDefaultCoupon}
                isInstallmentTerm={isInstallmentTerm}
              />
            ) : (
              <Price
                amount={amount}
                term={term}
                discountValue={displayDiscountValue}
                isDefaultCoupon={isDefaultCoupon}
              />
            )}

            <NameContainer>
              {/* Bundle names follow "Title — members"; the members render as their own smaller
                line so a six-certification Master wraps gracefully instead of running the title
                across three rows on mobile. Names without the separator render unchanged. */}
              <CourseNameText>{resolvedCourseName.split(' — ')[0]}</CourseNameText>
              <CourseSubNameText>{planLevelName}</CourseSubNameText>
            </NameContainer>
            {resolvedCourseName.includes(' — ') && (
              <CourseMembersText>{resolvedCourseName.split(' — ').slice(1).join(' — ')}</CourseMembersText>
            )}
            {isCertificate && isPaymentPlan ? (
              <CouponForCertificate
                subscribeType={subscribeType as SubscribeDtoType}
                isDefaultCoupon={isDefaultCoupon}
                defaultCouponCode={couponCode}
                setIsLoadingChangeFirstCoupon={setIsLoadingChangeFirstCoupon}
                isSuccessCoupon={isSuccessCoupon}
                courseName={resolvedCourseName}
                onCouponSubmit={onCouponSubmit}
                removeCoupon={removeCoupon}
                amount={amount}
                setClientSecret={setClientSecret}
                setInvoiceId={setInvoiceId}
                oldAmount={oldAmount}
                setCertificateFinalPrice={setCertificateFinalPrice}
                promoCode={promoCode}
                invoiceId={invoiceId}
                discountAmount={discountAmount}
                discountValue={discountValue}
                isLoadingPromocode={isLoading || isChangePromoCodeLoading || isCheckPromoCodeLoading}
                couponErrorMessage={couponErrorMessage}
                invalidCoupon={isInvalidCoupon}
                setClearCoupon={() => setIsInvalidCoupon(false)}
                handleChangePromoCode={handleChangePromoCodeWithDeferredApply}
                disable={isLoading || isChangePromoCodeLoading || isCertificateLoading}
                firstMonthDiscountId={resolvedFirstMonthDiscountId}
                firstMonthDiscountAmount={resolvedFirstMonthDiscountAmount}
                firstMonthDiscountType={resolvedFirstMonthDiscountType}
                firstMonthDiscountPromoCode={resolvedFirstMonthDiscountPromoCode}
              />
            ) : (
              <Coupon
                subscribeType={planLevelName}
                isDefaultCoupon={isDefaultCoupon}
                defaultCouponCode={couponCode}
                isSuccessCoupon={isSuccessCoupon}
                setCertificateFinalPrice={setCertificateFinalPrice}
                courseName={resolvedCourseName}
                onCouponSubmit={onCouponSubmit}
                removeCoupon={removeCoupon}
                amount={amount}
                oldAmount={oldAmount}
                promoCode={promoCode}
                discountAmount={discountAmount}
                discountValue={discountValue}
                couponErrorMessage={couponErrorMessage}
                invalidCoupon={isInvalidCoupon}
                setClearCoupon={() => setIsInvalidCoupon(false)}
                handleChangePromoCode={handleChangePromoCodeWithDeferredApply}
                disable={isLoading || isChangePromoCodeLoading || isCertificateLoading}
              />
            )}
          </HeaderContent>
        </PaymentWrapper>
        <Form onSubmit={handleSubmit}>
          <FormWrapper>
            {paymentMethod && finalPrice >= 1 && (
              <>
                <BankCard
                  onClick={() => handleSubmitStripe(undefined, paymentMethod)}
                  isLoading={
                    isLoadingStripe ||
                    !clientSecret ||
                    isLoading ||
                    isChangePromoCodeLoading ||
                    isLoadingChangeFirstCoupon ||
                    isCertificateLoading
                  }
                  card={paymentMethod?.card?.last4 ?? ''}
                  paymentSystem={paymentMethod?.card?.brand as PaymentsSystemsEnums}
                />
                <Text>Or with new bank card</Text>
              </>
            )}
            <PaymentFieldsWrapper>
              <StripeProvider
                setStripe={setStripe}
                setElements={(elements: StripeElements | null) => setElements(elements)}
                onConfirmExpressCheckout={handleSubmitStripe}
                amount={finalPrice * 100}
                mode={term === TermEnum.LIFE_TIME ? 'payment' : 'subscription'}
                clientSecret={clientSecretStripe}>
                <PaymentElement
                  options={{
                    wallets: { applePay: 'auto', googlePay: 'auto' },
                    terms: { card: 'never' },
                  }}
                />
              </StripeProvider>
              {errorMessageStripe && <ErrorText>{errorMessageStripe}</ErrorText>}
            </PaymentFieldsWrapper>
          </FormWrapper>
          <Secure />
          <StyledButton
            disabled={finalPrice < 1 || isDisabledStripe}
            isLoading={
              isLoadingStripe ||
              !clientSecret ||
              isLoading ||
              isChangePromoCodeLoading ||
              isLoadingChangeFirstCoupon ||
              isCertificateLoading
            }
            onClick={() => {
              const isPurchase = term === TermEnum.LIFE_TIME;
              const event = isPurchase ? `Lifetime ${planLevelName}` : planLevelName;
              trackAmplitudeEvent(`${event} Enroll Now Pressed`);
            }}
            variant="primary"
            size="middle"
            type="submit">
            {`Enroll Now: $${certificateFinalPrice || finalPrice} USD`}
          </StyledButton>
          <LinkContainer>
            <Link as="a" href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
              Privacy
            </Link>
            <Link as="a" href={termOfUseUrl} target="_blank" rel="noopener noreferrer">
              Terms
            </Link>
          </LinkContainer>
          <SecureTextContainer>
            {term !== TermEnum.LIFE_TIME && (
              <SecureText font="subhead_medium_13">Renews automatically. Cancel anytime.</SecureText>
            )}
          </SecureTextContainer>
        </Form>
      </DefaultModal>
    </MonetizationLightModeProvider>
  );
};

export default PaymentModal;

const ContainerCSS = css`
  width: 100%;
  overflow: scroll;
  height: fit-content;
  max-height: 100%;

  display: flex;
  flex-direction: column;
  padding: 0px 0 40px;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${respondToWidth.sm`
    width:100%;
    padding-left: 25px;
    overflow-x: hidden;
    padding-right: 25px;
  `}

  ${respondToWidth.s`
    height:100%;
    padding: 16px;
    overflow-x: hidden;
    border-radius:0;
  `}
`;

const CloseButton = css`
  top: 20px;
  border: 1px solid;
  border-color: #ffffff;
  color: #ffffff;
  background-color: ${({ theme: { colors } }) => colors.primary[1]};
`;

/* Keeps the real header mounted while skeletons show — CouponForCertificate/Coupon fire the
   default-coupon check from their own mount effect, so unmounting them would leave the skeleton
   waiting on a fetch that never starts. `contents` makes the wrapper layout-invisible. */
const HeaderContent = styled.div<{ $hidden: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'contents')};
`;

/* The close button's twin, top-left; stays visible on mobile (the base hides it under sm). */
const BackButton = css`
  top: 20px;
  border: 1px solid;
  border-color: #ffffff;
  color: #ffffff;
  background-color: ${({ theme: { colors } }) => colors.primary[1]};

  ${respondToWidth.sm`
    display: flex;
  `}
`;

const ModalCSS = css`
  width: 100%;
  max-width: 688px;

  height: 100%;
  padding: 22px 0;
  overflow: hidden;
  overflow-x: hidden;

  display: flex;
  align-items: center;

  ${respondToWidth.sm`
    max-width: none;
    width: 90%;
  `}

  ${respondToWidth.s`
    width: 100%;
    height: 100%;
    padding: 0;

  `}
`;

const SecureText = styled(StyledText)`
  color: ${({ theme: { colors } }) => colors.neutrals[4]};
  margin-left: 6px;
`;

const SecureTextContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  flex-grow: 1;
  gap: 15px;

  color: ${({ theme: { colors } }) => colors.neutrals[1]};

  padding-inline: 95px 100px;

  ${respondToWidth.sm`
    padding-inline: 16px;
  `}
`;

const NameContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
`;

const CourseNameText = styled.span`
  display: flex;
  justify-content: center;
  text-align: center;
  padding-bottom: 10px;
  cursor: pointer;
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.neutrals[11]};
    ${typography.title_5_bold_20}
  `};
`;

const CourseMembersText = styled.span`
  display: block;
  text-align: center;
  margin: -4px auto 10px;
  max-width: 480px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  opacity: 0.85;
  overflow-wrap: break-word;
  ${({ theme: { colors } }) => css`
    color: ${colors.neutrals[11]};
  `};
`;

const CourseSubNameText = styled.span`
  display: flex;
  height: 28px;
  padding: 0px 5px;
  margin-left: 10px;
  border-radius: 10px;
  margin-top: -10px;
  text-align: center;
  padding-bottom: 10px;
  cursor: default;
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.primary[1]};
    background-color: ${colors.neutrals[11]};
    ${typography.title_5_bold_20}
  `};
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${respondToWidth.sm`
      gap: 24px;
  `}
`;

const PaymentFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PaymentWrapper = styled.div`
  margin-top: -32px;
  margin-bottom: 35px;
  border-top-right-radius: 15px;
  border-top-left-radius: 15px;
  background-color: ${({ theme: { colors } }) => colors.primary[1]};

  ${respondToWidth.sm`
  margin-top: -42px;
  margin-left: -25px;
  margin-right: -25px;
`}
`;

const ErrorText = styled(StyledText)`
  text-align: start;

  ${({ theme: { colors, typography } }) => css`
    color: ${colors.system.red};
    ${typography.body_basic_medium_14}

    ${respondToWidth.s`
      ${typography.footnote_semibold_12}
  `}
  `};
`;

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 40px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 45px;
  margin-bottom: 35px;
  cursor: pointer;
  padding-right: 8px;

  ${respondToWidth.s`
  margin-top: 20px;
  margin-bottom: 23px;
`}
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
  ${respondToWidth.s`
    margin-top: auto;
  `}
`;

const Text = styled(StyledText)`
  text-align: center;
  color: ${({ theme: { colors } }) => colors.neutrals[4]};
  ${({ theme: { typography } }) => typography.body_basic_medium_14};
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const Link = styled(StyledText)`
  display: flex;
  justify-content: center;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 4px;

  color: ${({ theme: { colors } }) => colors.primary[1]};
`;
